/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { FormArray, FormGroup, Validators } from '@angular/forms';
import { BuilderFormSchemaFactory, FormRootSchema } from '@gh/builder-shared';
import { BuilderPermission } from '@gh/builder-shared-data';
import { FormState, FormStateSchema, NumberTypes, UserContext, whenCreateOrEditMode } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FormMode, getControl, getControlValue, setControlValue } from '@gh/core-ui';
import { findIndex, first, get, isEmpty, isNil, map } from 'lodash';
import { CostCenterDetails, CostCenterSupplier } from '../../resources/cost-center-details.model';

@Injectable()
export class CostCenterDetailsFormState extends FormState<CostCenterDetails> {
  constructor(schemaFactory: BuilderFormSchemaFactory,
              private userContext: UserContext) {
    super(schemaFactory);
  }

  get id(): ID {
    return getControlValue(this.group, 'id');
  }

  get preferredId(): ID {
    return getControlValue(this.group, 'preferredSupplier');
  }

  get contactIds(): ID[] {
    return <ID[]>map(getControlValue(this.group, 'suppliers'), 'contactId');
  }

  get suppliersControl(): FormArray {
    return <FormArray>this.group.get('suppliers');
  }

  get supplierCount(): number {
    return this.suppliersControl.length;
  }

  assignPreferred(supplier: CostCenterSupplier): void {
    const control = getControl(this.group, 'preferredSupplier');
    control.setValue(supplier.contactId);
    control.markAsDirty();
  }

  addSuppliers(suppliers: CostCenterSupplier[]): void {
    const { group } = this.schema;
    const supplierArray = <FormArray>this.group.get('suppliers');

    if (isEmpty(getControlValue(this.group, 'suppliers'))) {
      setControlValue(this.group, 'preferredSupplier', get(first(suppliers), 'contactId'));
    }

    suppliers.forEach((supplier) => {
      supplierArray.push(group({
        key: supplier.contactId,
        children: {
          contactId: void 0,
          supplierId: void 0,
          contactName: void 0,
        },
      }).build(supplier));
    });

    supplierArray.markAsDirty();
  }

  deleteSupplier(targetSupplier: CostCenterSupplier): void {
    const { contactId: targetId } = targetSupplier;
    const supplierArray = <FormArray>this.group.get('suppliers');

    const index = findIndex(
      supplierArray.controls,
      (supplier: FormGroup) => getControlValue(supplier, 'contactId') === targetId,
    );

    if (index >= 0) {
      supplierArray.removeAt(index);
    }

    supplierArray.markAsDirty();
  }

  protected buildSchema(details: CostCenterDetails, { root, array, group }: FormStateSchema): FormRootSchema {
    let mode;
    if (isNil(details.id)) {
      mode = FormMode.CreateMode;
    } else if (this.userContext.hasPermission(BuilderPermission.Setup.Estimating.CostCenters.EDIT)) {
      mode = FormMode.EditMode;
    } else {
      mode = FormMode.ViewMode;
    }

    return root({
      mode,
      enabled: whenCreateOrEditMode(),
      optimisticLock: true,
      children: {
        id: void 0,
        code: { required: true, type: NumberTypes.Code },
        name: { required: true },
        scheduledDays: { required: true, type: NumberTypes.Days, validators: [Validators.min(1)] },
        isInJobSchedule: void 0,
        preferredSupplier: void 0,
        suppliers: array({
          children: (details.suppliers || []).map((contact) => group({
            key: contact.contactId,
            children: {
              contactId: void 0,
              supplierId: void 0,
              contactName: void 0,
            },
          })),
        }),
      },
    });
  }
}
